import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "max-w-7xl mx-auto p-4 lg:p-6" };
const _hoisted_2 = { class: "block lg:hidden" };
const _hoisted_3 = { class: "flex flex-col gap-2 mb-3" };
const _hoisted_4 = { class: "text-center" };
const _hoisted_5 = { class: "hidden lg:flex lg:items-center lg:justify-between" };
const _hoisted_6 = { class: "flex items-center gap-3" };
const _hoisted_7 = {
    key: 0,
    class: "mt-6 border-t border-gray-200 pt-6 space-y-4"
};
const _hoisted_8 = { class: "bg-gray-50 p-4 rounded-lg" };
const _hoisted_9 = { class: "flex items-center justify-between mb-2" };
const _hoisted_10 = { class: "relative inline-flex items-center cursor-pointer" };
const _hoisted_11 = { class: "bg-gray-50 p-4 rounded-lg" };
const _hoisted_12 = { class: "flex items-center justify-between mb-2" };
const _hoisted_13 = { class: "relative inline-flex items-center cursor-pointer" };
const _hoisted_14 = { class: "bg-gray-50 p-4 rounded-lg" };
const _hoisted_15 = { class: "flex items-center justify-between mb-2" };
const _hoisted_16 = { class: "relative inline-flex items-center cursor-pointer" };
const _hoisted_17 = { class: "flex flex-col sm:flex-row gap-3 pt-4" };
const _hoisted_18 = { class: "pt-4 border-t border-gray-200 text-center" };
const _hoisted_19 = { class: "text-xs text-gray-500" };
import { ref, reactive, onMounted, onBeforeUnmount, inject, computed } from 'vue';
import { useCookieManager } from '../composables/useCookieManager';
import { useTheme } from '../composables/useTheme';
// Cookie consent state
const CONSENT_VERSION = '1.0'; // Increment when cookie policy changes
// Check if user has already given consent
export default /*@__PURE__*/ _defineComponent({
    __name: 'CookieConsent',
    setup(__props, { expose: __expose }) {
        const showBanner = ref(false);
        const showSettings = ref(false);
        const projectConfig = inject('projectConfig', null);
        const projectIdentifier = projectConfig?.projectIdentifier || 'app';
        const sharedCookieManager = useCookieManager({ projectIdentifier });
        const { theme } = useTheme();
        const primaryColor = computed(() => theme.value.colors.primary || '#2563eb');
        const primaryButtonStyle = computed(() => ({
            backgroundColor: primaryColor.value
        }));
        const primaryTextStyle = computed(() => ({
            color: primaryColor.value
        }));
        const getToggleTrackStyle = (enabled) => ({
            backgroundColor: enabled ? primaryColor.value : '#e5e7eb'
        });
        const getToggleThumbStyle = (enabled) => ({
            transform: enabled ? 'translateX(20px)' : 'translateX(0px)'
        });
        // Cookie settings
        const cookieSettings = reactive({
            essential: true, // Always true, cannot be disabled
            analytics: false,
            marketing: false,
            externalContent: false,
            functional: false
        });
        // Cookie names for storage
        const COOKIE_CONSENT_KEY = `${projectIdentifier.replace(/\./g, '_')}_cookie_consent`;
        const COOKIE_SETTINGS_KEY = `${projectIdentifier.replace(/\./g, '_')}_cookie_settings`;
        const hasValidConsent = () => {
            try {
                const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
                if (!stored)
                    return false;
                const consent = JSON.parse(stored);
                // Check if consent is current version and not older than 1 year
                const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
                return consent.version === CONSENT_VERSION && consent.timestamp > oneYearAgo;
            }
            catch {
                return false;
            }
        };
        // Load existing settings
        const loadExistingSettings = () => {
            try {
                const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
                if (stored) {
                    const consent = JSON.parse(stored);
                    const normalized = {
                        ...consent.settings,
                        externalContent: consent.settings?.externalContent ?? consent.settings?.marketing ?? false
                    };
                    consent.settings = normalized;
                    Object.assign(cookieSettings, consent.settings);
                }
            }
            catch {
                // Use default settings if loading fails
            }
        };
        // Save consent to localStorage
        const saveConsent = () => {
            const consent = {
                version: CONSENT_VERSION,
                timestamp: Date.now(),
                settings: { ...cookieSettings }
            };
            try {
                localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
                localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(cookieSettings));
                // Keep the shared manager in sync so consent updates are reflected immediately.
                sharedCookieManager.saveConsent({ ...cookieSettings });
                sharedCookieManager.applySettings();
            }
            catch (error) {
                console.warn('Could not save cookie consent:', error);
            }
        };
        // Apply cookie settings to actual cookies/tracking
        const applyCookieSettings = () => {
            // Essential cookies - always enabled
            document.cookie = `essential_enabled=true; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=strict`;
            // Analytics cookies
            if (cookieSettings.analytics) {
                document.cookie = `analytics_enabled=true; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=lax`;
                // Initialize analytics (Google Analytics, etc.)
                initializeAnalytics();
            }
            else {
                document.cookie = `analytics_enabled=false; path=/; max-age=0`;
                disableAnalytics();
            }
            // Marketing cookies
            if (cookieSettings.marketing) {
                document.cookie = `marketing_enabled=true; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=lax`;
                initializeMarketing();
            }
            else {
                document.cookie = `marketing_enabled=false; path=/; max-age=0`;
                disableMarketing();
            }
            // External content cookies
            if (cookieSettings.externalContent) {
                document.cookie = `external_content_enabled=true; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=lax`;
            }
            else {
                document.cookie = `external_content_enabled=false; path=/; max-age=0`;
            }
            // Functional cookies
            if (cookieSettings.functional) {
                document.cookie = `functional_enabled=true; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=lax`;
                // Initialize functional features (chat widgets, etc.)
                initializeFunctional();
            }
            else {
                document.cookie = `functional_enabled=false; path=/; max-age=0`;
                disableFunctional();
            }
        };
        // Initialize analytics (placeholder)
        const initializeAnalytics = () => {
            console.log('🔍 Analytics cookies enabled');
            // TODO: Initialize Google Analytics, Matomo, etc.
            // Example:
            // gtag('consent', 'update', { analytics_storage: 'granted' })
        };
        const disableAnalytics = () => {
            console.log('🔍 Analytics cookies disabled');
            // TODO: Disable analytics tracking
            // gtag('consent', 'update', { analytics_storage: 'denied' })
        };
        // Initialize marketing (placeholder)
        const initializeMarketing = () => {
            console.log('📢 Marketing cookies enabled');
            // TODO: Initialize Facebook Pixel, Google Ads, etc.
            // Example:
            // gtag('consent', 'update', { ad_storage: 'granted' })
        };
        const disableMarketing = () => {
            console.log('📢 Marketing cookies disabled');
            // TODO: Disable marketing tracking
            // gtag('consent', 'update', { ad_storage: 'denied' })
        };
        // Initialize functional features (placeholder)
        const initializeFunctional = () => {
            console.log('⚙️ Functional cookies enabled');
            // TODO: Initialize chat widgets, preference storage, etc.
        };
        const disableFunctional = () => {
            console.log('⚙️ Functional cookies disabled');
            // TODO: Disable functional features
        };
        // Accept all cookies
        const acceptAll = () => {
            cookieSettings.analytics = true;
            cookieSettings.marketing = true;
            cookieSettings.externalContent = true;
            cookieSettings.functional = true;
            saveConsent();
            applyCookieSettings();
            hideBanner();
        };
        // Accept only required cookies
        const acceptRequired = () => {
            cookieSettings.analytics = false;
            cookieSettings.marketing = false;
            cookieSettings.externalContent = false;
            cookieSettings.functional = false;
            saveConsent();
            applyCookieSettings();
            hideBanner();
        };
        // Save custom settings
        const saveSettings = () => {
            saveConsent();
            applyCookieSettings();
            hideBanner();
        };
        // Hide banner
        const hideBanner = () => {
            showBanner.value = false;
            showSettings.value = false;
        };
        // Expose method to show banner (for settings page)
        const showCookieBanner = () => {
            loadExistingSettings();
            showBanner.value = true;
        };
        const openCookieSettings = () => {
            loadExistingSettings();
            showBanner.value = true;
            showSettings.value = true;
        };
        // Initialize on component mount
        onMounted(() => {
            // Load existing settings if available
            loadExistingSettings();
            // Show banner if no valid consent exists
            if (!hasValidConsent()) {
                // Delay banner show for better UX
                setTimeout(() => {
                    showBanner.value = true;
                }, 1000);
            }
            else {
                // Apply existing settings
                applyCookieSettings();
            }
            window.addEventListener('open-cookie-settings', openCookieSettings);
        });
        onBeforeUnmount(() => {
            window.removeEventListener('open-cookie-settings', openCookieSettings);
        });
        // Export for use in other components
        __expose({
            showCookieBanner,
            cookieSettings
        });
        return (_ctx, _cache) => {
            const _component_router_link = _resolveComponent("router-link");
            return (showBanner.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 transform transition-transform duration-300", { 'translate-y-full': !showBanner.value }])
                }, [
                    _createElementVNode("div", _hoisted_1, [
                        _createCommentVNode(" Mobile Layout "),
                        _createElementVNode("div", _hoisted_2, [
                            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "text-center mb-4" }, [
                                _createElementVNode("h3", { class: "text-lg font-bold text-gray-900 mb-2" }, "🍪 Cookie-Einstellungen"),
                                _createElementVNode("p", { class: "text-sm text-gray-700 leading-relaxed" }, " Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. Einige sind für den Betrieb der Website erforderlich, andere helfen uns dabei, unsere Inhalte zu verbessern. ")
                            ], -1 /* CACHED */)),
                            _createCommentVNode(" Quick Actions Mobile "),
                            _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("button", {
                                    onClick: acceptAll,
                                    class: "w-full text-white px-4 py-3 rounded-lg font-semibold transition-colors duration-200",
                                    style: _normalizeStyle(primaryButtonStyle.value)
                                }, " Alle akzeptieren ", 4 /* STYLE */),
                                _createElementVNode("button", {
                                    onClick: acceptRequired,
                                    class: "w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-semibold transition-colors duration-200"
                                }, " Nur erforderliche ")
                            ]),
                            _createCommentVNode(" Settings Link Mobile "),
                            _createElementVNode("div", _hoisted_4, [
                                _createElementVNode("button", {
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showSettings.value = !showSettings.value)),
                                    class: "underline text-sm font-medium",
                                    style: _normalizeStyle(primaryTextStyle.value)
                                }, _toDisplayString(showSettings.value ? 'Einstellungen schließen' : 'Cookie-Einstellungen anpassen'), 5 /* TEXT, STYLE */)
                            ])
                        ]),
                        _createCommentVNode(" Desktop Layout "),
                        _createElementVNode("div", _hoisted_5, [
                            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "flex-1 mr-6" }, [
                                _createElementVNode("h3", { class: "text-lg font-bold text-gray-900 mb-2" }, "🍪 Cookie-Einstellungen"),
                                _createElementVNode("p", { class: "text-sm text-gray-700 leading-relaxed" }, " Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. Einige sind für den Betrieb der Website erforderlich, andere helfen uns dabei, unsere Inhalte zu verbessern. ")
                            ], -1 /* CACHED */)),
                            _createElementVNode("div", _hoisted_6, [
                                _createElementVNode("button", {
                                    onClick: _cache[1] || (_cache[1] = ($event) => (showSettings.value = !showSettings.value)),
                                    class: "underline text-sm font-medium whitespace-nowrap",
                                    style: _normalizeStyle(primaryTextStyle.value)
                                }, " Einstellungen ", 4 /* STYLE */),
                                _createElementVNode("button", {
                                    onClick: acceptRequired,
                                    class: "bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap"
                                }, " Nur erforderliche "),
                                _createElementVNode("button", {
                                    onClick: acceptAll,
                                    class: "text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap",
                                    style: _normalizeStyle(primaryButtonStyle.value)
                                }, " Alle akzeptieren ", 4 /* STYLE */)
                            ])
                        ]),
                        _createCommentVNode(" Detailed Settings "),
                        (showSettings.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _cache[22] || (_cache[22] = _createElementVNode("h4", { class: "text-lg font-semibold text-gray-900 mb-4" }, "Detaillierte Cookie-Einstellungen", -1 /* CACHED */)),
                                _createCommentVNode(" Essential Cookies "),
                                _cache[23] || (_cache[23] = _createStaticVNode("<div class=\"bg-gray-50 p-4 rounded-lg\" data-v-298a805a><div class=\"flex items-center justify-between mb-2\" data-v-298a805a><h5 class=\"font-semibold text-gray-900\" data-v-298a805a>Erforderliche Cookies</h5><span class=\"bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium\" data-v-298a805a> Immer aktiv </span></div><p class=\"text-sm text-gray-600 mb-2\" data-v-298a805a> Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. </p><ul class=\"text-xs text-gray-500 space-y-1\" data-v-298a805a><li data-v-298a805a>• Session-Management (Warenkorb, Bestellungen)</li><li data-v-298a805a>• Sicherheits-Token (CSRF-Schutz)</li><li data-v-298a805a>• Cookie-Consent-Status</li></ul></div>", 1)),
                                _createCommentVNode(" Analytics Cookies "),
                                _createElementVNode("div", _hoisted_8, [
                                    _createElementVNode("div", _hoisted_9, [
                                        _cache[8] || (_cache[8] = _createElementVNode("h5", { class: "font-semibold text-gray-900" }, "Analyse-Cookies", -1 /* CACHED */)),
                                        _createElementVNode("label", _hoisted_10, [
                                            _withDirectives(_createElementVNode("input", {
                                                type: "checkbox",
                                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((cookieSettings.analytics) = $event)),
                                                class: "sr-only peer"
                                            }, null, 512 /* NEED_PATCH */), [
                                                [_vModelCheckbox, cookieSettings.analytics]
                                            ]),
                                            _createElementVNode("div", {
                                                class: "w-11 h-6 rounded-full transition-colors duration-200 relative",
                                                style: _normalizeStyle(getToggleTrackStyle(cookieSettings.analytics))
                                            }, [
                                                _createElementVNode("div", {
                                                    class: "absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform duration-200",
                                                    style: _normalizeStyle(getToggleThumbStyle(cookieSettings.analytics))
                                                }, null, 4 /* STYLE */)
                                            ], 4 /* STYLE */)
                                        ])
                                    ]),
                                    _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm text-gray-600 mb-2" }, " Helfen uns dabei, zu verstehen, wie Besucher mit der Website interagieren. ", -1 /* CACHED */)),
                                    _cache[10] || (_cache[10] = _createElementVNode("ul", { class: "text-xs text-gray-500 space-y-1" }, [
                                        _createElementVNode("li", null, "• Besucherstatistiken (anonymisiert)"),
                                        _createElementVNode("li", null, "• Seitenaufrufe und Verweildauer"),
                                        _createElementVNode("li", null, "• Beliebte Produkte und Kategorien")
                                    ], -1 /* CACHED */))
                                ]),
                                _createCommentVNode(" External Content "),
                                _createElementVNode("div", _hoisted_11, [
                                    _createElementVNode("div", _hoisted_12, [
                                        _cache[11] || (_cache[11] = _createElementVNode("h5", { class: "font-semibold text-gray-900" }, "Externe Inhalte", -1 /* CACHED */)),
                                        _createElementVNode("label", _hoisted_13, [
                                            _withDirectives(_createElementVNode("input", {
                                                type: "checkbox",
                                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((cookieSettings.externalContent) = $event)),
                                                class: "sr-only peer"
                                            }, null, 512 /* NEED_PATCH */), [
                                                [_vModelCheckbox, cookieSettings.externalContent]
                                            ]),
                                            _createElementVNode("div", {
                                                class: "w-11 h-6 rounded-full transition-colors duration-200 relative",
                                                style: _normalizeStyle(getToggleTrackStyle(cookieSettings.externalContent))
                                            }, [
                                                _createElementVNode("div", {
                                                    class: "absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform duration-200",
                                                    style: _normalizeStyle(getToggleThumbStyle(cookieSettings.externalContent))
                                                }, null, 4 /* STYLE */)
                                            ], 4 /* STYLE */)
                                        ])
                                    ]),
                                    _cache[12] || (_cache[12] = _createElementVNode("p", { class: "text-sm text-gray-600 mb-2" }, " Erlauben das Laden externer Inhalte wie Google Bewertungen. ", -1 /* CACHED */)),
                                    _cache[13] || (_cache[13] = _createElementVNode("ul", { class: "text-xs text-gray-500 space-y-1" }, [
                                        _createElementVNode("li", null, "• Google Bewertungen anzeigen"),
                                        _createElementVNode("li", null, "• Inhalte von Drittanbietern"),
                                        _createElementVNode("li", null, "• Erweiterte externe Widgets")
                                    ], -1 /* CACHED */))
                                ]),
                                _createCommentVNode(" Functional Cookies "),
                                _createElementVNode("div", _hoisted_14, [
                                    _createElementVNode("div", _hoisted_15, [
                                        _cache[14] || (_cache[14] = _createElementVNode("h5", { class: "font-semibold text-gray-900" }, "Funktions-Cookies", -1 /* CACHED */)),
                                        _createElementVNode("label", _hoisted_16, [
                                            _withDirectives(_createElementVNode("input", {
                                                type: "checkbox",
                                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((cookieSettings.functional) = $event)),
                                                class: "sr-only peer"
                                            }, null, 512 /* NEED_PATCH */), [
                                                [_vModelCheckbox, cookieSettings.functional]
                                            ]),
                                            _createElementVNode("div", {
                                                class: "w-11 h-6 rounded-full transition-colors duration-200 relative",
                                                style: _normalizeStyle(getToggleTrackStyle(cookieSettings.functional))
                                            }, [
                                                _createElementVNode("div", {
                                                    class: "absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform duration-200",
                                                    style: _normalizeStyle(getToggleThumbStyle(cookieSettings.functional))
                                                }, null, 4 /* STYLE */)
                                            ], 4 /* STYLE */)
                                        ])
                                    ]),
                                    _cache[15] || (_cache[15] = _createElementVNode("p", { class: "text-sm text-gray-600 mb-2" }, " Verbessern die Funktionalität und Personalisierung der Website. ", -1 /* CACHED */)),
                                    _cache[16] || (_cache[16] = _createElementVNode("ul", { class: "text-xs text-gray-500 space-y-1" }, [
                                        _createElementVNode("li", null, "• Spracheinstellungen"),
                                        _createElementVNode("li", null, "• Präferenzen für Darstellung"),
                                        _createElementVNode("li", null, "• Chat-Widget und Support")
                                    ], -1 /* CACHED */))
                                ]),
                                _createCommentVNode(" Action Buttons in Settings "),
                                _createElementVNode("div", _hoisted_17, [
                                    _createElementVNode("button", {
                                        onClick: saveSettings,
                                        class: "flex-1 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200",
                                        style: _normalizeStyle(primaryButtonStyle.value)
                                    }, " Einstellungen speichern ", 4 /* STYLE */),
                                    _createElementVNode("button", {
                                        onClick: _cache[5] || (_cache[5] = ($event) => (showSettings.value = false)),
                                        class: "flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                                    }, " Abbrechen ")
                                ]),
                                _createCommentVNode(" Legal Links "),
                                _createElementVNode("div", _hoisted_18, [
                                    _createElementVNode("p", _hoisted_19, [
                                        _cache[19] || (_cache[19] = _createTextVNode(" Weitere Informationen finden Sie in unserer ", -1 /* CACHED */)),
                                        _createVNode(_component_router_link, {
                                            to: "/impressum?tab=datenschutz",
                                            class: "underline",
                                            style: _normalizeStyle(primaryTextStyle.value)
                                        }, {
                                            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                                    _createTextVNode("Datenschutzerklärung", -1 /* CACHED */)
                                                ]))]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["style"]),
                                        _cache[20] || (_cache[20] = _createTextVNode(" und im ", -1 /* CACHED */)),
                                        _createVNode(_component_router_link, {
                                            to: "/impressum",
                                            class: "underline",
                                            style: _normalizeStyle(primaryTextStyle.value)
                                        }, {
                                            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                                    _createTextVNode("Impressum", -1 /* CACHED */)
                                                ]))]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["style"]),
                                        _cache[21] || (_cache[21] = _createTextVNode(". ", -1 /* CACHED */))
                                    ])
                                ])
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ], 2 /* CLASS */))
                : _createCommentVNode("v-if", true);
        };
    }
});
